import {
  Forbidden,
  PageNotFound,
} from '@risksmart-app/components/src/errors/errors';
import type { ErrorEvent, EventHint } from '@sentry/react';
import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';

export const addAxiosContext = (event: ErrorEvent, error: AxiosError) => {
  if (error.response) {
    const contexts = { ...event.contexts };
    contexts.Axios = { Response: error.response };
    event.contexts = contexts;
  }
};

export const addAxiosContextRecursive = (event: ErrorEvent, error: unknown) => {
  if (isAxiosError(error)) {
    addAxiosContext(event, error);
  } else if (error instanceof Error && error.cause) {
    addAxiosContextRecursive(event, error.cause);
  }
};

export const isExpectedRouteError = (error: unknown): boolean => {
  if (error instanceof PageNotFound || error instanceof Forbidden) {
    return true;
  }
  if (error instanceof Error && error.cause) {
    return isExpectedRouteError(error.cause);
  }

  return false;
};

// Error messages dropped client-side before they reach Sentry. Every entry must
// cite the Sentry short-id it addresses and why it is safe to drop. Strings are
// substring-matched; keep them specific enough to never match an app error.
export const sentryIgnoreErrors: (string | RegExp)[] = [
  // RISKSMART-APP-5YK — Google Tag Manager blocked by ad-blockers / corporate
  // proxies. The rejection fires inside Segment's load-script helper with no app
  // frames; 81 users produced 51k+ lifetime events (per-page-load retry noise).
  /^Failed to load https:\/\/www\.googletagmanager\.com/,
  // RISKSMART-APP-43 — benign browser-emitted ResizeObserver warning with no app
  // frames. Already archived-forever in Sentry but still consumes ingestion quota.
  'ResizeObserver loop completed with undelivered notifications',
  // RISKSMART-APP-A1D — @auth0/auth0-react getTokenSilently rejecting for sessions
  // whose refresh token is gone (expired/revoked). All frames inside the Auth0 SDK;
  // the app already recovers by redirecting to login.
  'Missing Refresh Token',
];

// Browser fetch() rejections carry no response and one of these engine-specific
// messages (Chromium / WebKit / Firefox respectively).
const networkFetchErrorMessages = [
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
];

// RISKSMART-APP-8SC — GraphQL/REST fetches rejecting while the user's connection
// drops. Only dropped when the browser itself reports offline, so CORS, deploy and
// server failures (which also surface as fetch TypeErrors) still report online.
export const isOfflineNetworkError = (error: unknown): boolean =>
  error instanceof TypeError &&
  networkFetchErrorMessages.some((message) =>
    error.message.includes(message)
  ) &&
  typeof navigator !== 'undefined' &&
  navigator.onLine === false;

export const handleSentryBeforeSend = (event: ErrorEvent, hint: EventHint) => {
  if (isExpectedRouteError(hint?.originalException)) {
    return null;
  }

  if (isOfflineNetworkError(hint?.originalException)) {
    return null;
  }

  addAxiosContextRecursive(event, hint?.originalException);

  return event;
};
